import { useCallback } from "react";
import Button from "@mui/material/Button";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import MenuItem from "@mui/material/MenuItem";
import Select, { type SelectChangeEvent } from "@mui/material/Select";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import { FIELD_REANNOTATION_FIELDS } from "@shared/types/corpus";
import { Note } from "../../../components/primitives/data-display/Note";
import { ConfirmDialog } from "../../../components/primitives/feedback/ConfirmDialog";
import { Panel } from "../../../components/primitives/surfaces/Panel";
import type { FieldReannotationField } from "../../../lib/corpusApi";
import { fieldLabel } from "../../../lib/scorecardFieldUtils";
import { CORPUS_SCAN_HEADINGS } from "./corpus_scan_headings";
import { CorpusScanPanel } from "./CorpusScanPanel";
import { describeMissingFieldScan } from "./missing_field_scan_views";
import type { UseMissingFieldScanResult } from "./useMissingFieldScan";

const FIELD_OPTIONS = FIELD_REANNOTATION_FIELDS.map((value) => ({
  value,
  label: fieldLabel(value),
}));

interface MissingFieldScanProps {
  scan: UseMissingFieldScanResult;
}

/** The scan for the documents with no label for one chosen scorecard field, with the button that
 *  re-annotates that field on them. */
export function MissingFieldScan({ scan }: MissingFieldScanProps) {
  const { field, setField, result, scan: run, launch, dialogProps } = scan;
  const view = result ? describeMissingFieldScan(result) : null;

  const handleFieldChange = useCallback(
    (e: SelectChangeEvent) => setField(e.target.value as FieldReannotationField),
    [setField]
  );

  return (
    <>
      <CorpusScanPanel
        {...CORPUS_SCAN_HEADINGS.missing_field}
        fields={
          <FormControl size="small" sx={{ minWidth: 240 }}>
            <InputLabel id="missing-field-scan-field-label">Field</InputLabel>
            <Select
              labelId="missing-field-scan-field-label"
              label="Field"
              value={field}
              onChange={handleFieldChange}
            >
              {FIELD_OPTIONS.map((opt) => (
                <MenuItem key={opt.value} value={opt.value}>
                  {opt.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        }
        runLabel="Scan documents"
        onRun={() => void run.handle()}
        running={run.loading}
        error={run.error}
        results={
          view ? (
            <Panel>
              <Stack gap={1}>
                <Stack direction="row" justifyContent="space-between" alignItems="center">
                  <Typography variant="body1">{view.summary}</Typography>
                  {view.launchLabel ? (
                    <Button variant="outlined" onClick={launch}>
                      {view.launchLabel}
                    </Button>
                  ) : null}
                </Stack>
                <Note>
                  A vocabulary-backed field (peer review, funding) is usually empty because the
                  vocabulary did not know the journal or funder yet: run the taxonomy triage before
                  re-annotating.
                </Note>
              </Stack>
            </Panel>
          ) : null
        }
      />
      <ConfirmDialog {...dialogProps} />
    </>
  );
}
